import React, { useEffect, useMemo, useState } from "react";
import { db } from "@/config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import Papa from "papaparse";
import Tesseract from "tesseract.js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

export default function StatsImport() {
  const [csvText, setCsvText] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [status, setStatus] = useState("");
  const [imageResult, setImageResult] = useState([]);
  const [gameMeta, setGameMeta] = useState({ event_id:"", final_home_score:"", final_away_score:"", final_technicals:"", title:"", opponent:"", league:"", location:"", start_datetime:"" });
  const [users, setUsers] = useState([]);
  const [dbbUrl, setDbbUrl] = useState("");
  const [loadingDBB, setLoadingDBB] = useState(false);
  const [jerseyMapping, setJerseyMapping] = useState(new Map()); // Trikotnummer → {name, uid}
  const [processingScreenshots, setProcessingScreenshots] = useState(false);
  const [persistedMappings, setPersistedMappings] = useState([]); // From dbb_player_mappings
  const [unresolvedPlayers, setUnresolvedPlayers] = useState([]); // Players needing mapping
  const [mappingSelections, setMappingSelections] = useState({}); // jersey -> selected userId

  // Load users and persisted player mappings
  useEffect(()=>{(async()=>{
    const us = await getDocs(collection(db, "users"));
    setUsers(us.docs.map(d=>({ id:d.id, ...d.data()})));
    
    // Load persisted player mappings from backend (requires admin auth)
    try {
      const user = await import("@/config/firebase").then(m => m.auth.currentUser);
      if (user) {
        const token = await user.getIdToken();
        const res = await fetch(`${BACKEND_URL}/api/admin/player-mappings?season_id=2026-27`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPersistedMappings(data.mappings || []);
          // Populate jerseyMapping from persisted
          const newMap = new Map();
          for (const m of (data.mappings || [])) {
            if (m.jersey_number) {
              newMap.set(m.jersey_number, { 
                name: m.internal_user_name || '', 
                uid: m.internal_user_id 
              });
            }
          }
          setJerseyMapping(prev => {
            const merged = new Map(prev);
            for (const [k, v] of newMap) merged.set(k, v);
            return merged;
          });
        }
      }
    } catch (err) {
      console.warn("Could not load player mappings:", err);
    }
  })();},[]);

  const usersByName = useMemo(()=>{
    const m = new Map();
    for (const u of users) {
      const key = (u.display_name||u.name||u.email||"").toLowerCase();
      if (!key) continue; m.set(key, u);
    }
    return m;
  },[users]);

  const parseCSV = () => { try { const out = Papa.parse(csvText, { header:true, skipEmptyLines:true }); return out.data || []; } catch { return []; } };
  const parseJSON = () => { try { return JSON.parse(jsonText || "[]"); } catch { return []; } };

  // Save a player mapping to the backend
  const savePlayerMapping = async (jerseyNumber, userId, dbbName = 'Player') => {
    try {
      const user = await import("@/config/firebase").then(m => m.auth.currentUser);
      if (!user) {
        setStatus("Nicht eingeloggt - kann Mapping nicht speichern");
        return false;
      }
      
      const token = await user.getIdToken();
      const res = await fetch(`${BACKEND_URL}/api/admin/player-mappings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jersey_number: jerseyNumber,
          dbb_name: dbbName,
          internal_user_id: userId,
          season_id: '2026-27',
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        // Update local state
        const selectedUser = users.find(u => (u.auth_uid || u.id) === userId);
        const userName = selectedUser?.display_name || selectedUser?.name || '';
        
        setJerseyMapping(prev => {
          const newMap = new Map(prev);
          newMap.set(jerseyNumber, { name: userName, uid: userId });
          return newMap;
        });
        
        // Remove from unresolved
        setUnresolvedPlayers(prev => prev.filter(p => p.jersey_number !== jerseyNumber));
        
        setStatus(`✅ Mapping gespeichert: #${jerseyNumber} → ${userName}`);
        return true;
      } else {
        const err = await res.json().catch(() => ({}));
        setStatus(`❌ Mapping fehlgeschlagen: ${err.detail || res.status}`);
        return false;
      }
    } catch (err) {
      setStatus(`❌ Mapping-Fehler: ${err.message}`);
      return false;
    }
  };

  const tryDetectScore = (text) => {
    const m = text.match(/(\d{1,3})\s*[:\-]\s*(\d{1,3})/);
    return m ? { h: m[1], a: m[2] } : null;
  };

  const parseFromDBBHtml = (html) => {
    const text = html.replace(/<script[\s\S]*?<\/script>/gi,' ').replace(/<style[\s\S]*?<\/style>/gi,' ').replace(/<[^>]+>/g, ' ').replace(/\s+/g,' ').trim();
    const score = tryDetectScore(text);
    if (score) setGameMeta(m=>({...m, final_home_score: score.h, final_away_score: score.a }));
    const rows = [];
    // robust name pattern (allow hyphen, umlauts), minutes and points following
    const re = /([A-ZÄÖÜ][A-Za-zÄÖÜäöüß\-]+\s+[A-ZÄÖÜ][A-Za-zÄÖÜäöüß\-]+)\s+(\d{1,2}[:]\d{2}|\d{1,2})\s+(\d{1,2})/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      const name = m[1].trim();
      const minutes = m[2];
      const points = parseInt(m[3],10);
      // FW m/a
      const around = text.slice(m.index, m.index+180);
      const fw = around.match(/(\d{1,2})\/(\d{1,2})/);
      const fw_m = fw? parseInt(fw[1],10):0;
      const fw_a = fw? parseInt(fw[2],10):0;
      // 2P/3P (heuristisch: nächste zwei kleine Zahlen nach Punkten, die nicht die FW sind)
      const ints = (around.match(/\b(\d{1,2})\b/g)||[]).map(Number);
      let twos = 0, threes = 0;
      if (ints.length>=4) { twos = ints[2]; threes = ints[3]; }
      // PF
      let pf = 0; const pfm = around.match(/(?:PF|Fouls)\s*(\d{1,2})/i); if (pfm) pf = parseInt(pfm[1],10);
      // plus/minus: letzte +/- Zahl im Segment
      let plus_minus = 0; const pmm = around.match(/([\-+]?\d{1,2})\s*$/); if (pmm) plus_minus = parseInt(pmm[1],10);
      const uid = usersByName.get(name.toLowerCase())?.auth_uid || usersByName.get(name.toLowerCase())?.id || "";
      rows.push({ name, player_id: uid, game_id: gameMeta.event_id, minutes: (minutes.includes(':')? minutes: Number(minutes)), points, plus_minus, fouls: pf, twos, threes, free_throws_made: fw_m, free_throws_att: fw_a, source: 'dbb-link' });
    }
    return rows;
  };

  // Multi-Screenshot-Verarbeitung mit intelligentem Mapping
  const onMultipleImages = async (e) => {
    const files = Array.from(e.target.files || []); 
    if (files.length === 0) return;
    
    setProcessingScreenshots(true);
    setStatus(`Verarbeite ${files.length} Screenshot(s)…`);
    
    const tempMapping = new Map(jerseyMapping);
    const allPlayerData = new Map(); // jersey → {name, uid, ...stats}
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setStatus(`Verarbeite Screenshot ${i+1}/${files.length}…`);
        
        const { data } = await Tesseract.recognize(file, 'deu+eng', { logger: m => {} });
        const full = data.text || "";
        
        // Endstand erkennen (nur beim ersten Screenshot)
        if (i === 0) {
          const score = tryDetectScore(full);
          if (score) setGameMeta(m => ({ ...m, final_home_score: score.h, final_away_score: score.a }));
        }
        
        const lines = full.split(/\n+/).map(s => s.trim()).filter(Boolean);
        
        for (const ln of lines) {
          // Trikotnummer am Anfang der Zeile finden
          const jerseyMatch = ln.match(/^(\d{2})\s/);
          if (!jerseyMatch) continue;
          
          const jersey = jerseyMatch[1];
          
          // Spieler-Datensatz initialisieren oder holen
          if (!allPlayerData.has(jersey)) {
            allPlayerData.set(jersey, {
              jersey,
              name: tempMapping.has(jersey) ? tempMapping.get(jersey).name : `Spieler #${jersey}`,
              player_id: tempMapping.has(jersey) ? tempMapping.get(jersey).uid : "",
              game_id: gameMeta.event_id,
              minutes: 0,
              points: 0,
              plus_minus: 0,
              fouls: 0,
              twos: 0,
              threes: 0,
              free_throws_made: 0,
              free_throws_att: 0
            });
          }
          
          const player = allPlayerData.get(jersey);
          
          // Pattern 1: Name nach Trikotnummer (nur im ersten Screenshot)
          const nameMatch = ln.match(/^\d{2}\s+([A-ZÄÖÜ][A-Za-zÄÖÜäöüß\-]+(?:\s+[A-ZÄÖÜ][A-Za-zÄÖÜäöüß\-]+)?)/);
          if (nameMatch) {
            const name = nameMatch[1].trim();
            const uid = usersByName.get(name.toLowerCase())?.auth_uid || usersByName.get(name.toLowerCase())?.id || "";
            tempMapping.set(jersey, { name, uid });
            player.name = name;
            player.player_id = uid;
          }
          
          // Alle Zahlen in der Zeile extrahieren
          const allNumbers = ln.match(/\d{1,2}:\d{2}|\d{1,3}%|\d{1,2}\/\d{1,2}|[\-+]?\d{1,2}/g) || [];
          
          // Minuten (MM:SS Format)
          const minutesMatch = ln.match(/(\d{1,2}):(\d{2})/);
          if (minutesMatch && !player.minutes) {
            player.minutes = minutesMatch[0];
          }
          
          // Punkte - Zahl nach Minuten oder nach "Pkt"
          const pointsMatch = ln.match(/\d{1,2}:\d{2}\s+(\d{1,2})|Pkt\s+(\d{1,2})/);
          if (pointsMatch && !player.points) {
            player.points = Number(pointsMatch[1] || pointsMatch[2] || 0);
          }
          
          // Freiwürfe (X/Y Format)
          const fwMatch = ln.match(/(\d{1,2})\/(\d{1,2})/);
          if (fwMatch && player.free_throws_made === 0) {
            player.free_throws_made = Number(fwMatch[1]);
            player.free_throws_att = Number(fwMatch[2]);
          }
          
          // FW% - nach dem Namen
          const fwPercMatch = ln.match(/(\d{1,3})%/);
          if (fwPercMatch) {
            // Speichern wir nur zur Info, wird nicht in DB gespeichert
            player.fw_perc = fwPercMatch[1];
          }
          
          // 2Pkt, 3Pkt, PF - versuchen wir aus der Position zu erkennen
          // In Screenshot 2: Nach FW% kommen 2Pkt, 3Pkt, PF
          // In Screenshot 3: 2Pkt, 3Pkt, PF, +/-
          const plainNumbers = ln.match(/(?<![\d:])\b(\d{1,2})\b(?![:\/%])/g);
          if (plainNumbers && plainNumbers.length >= 4) {
            // [0] = Jersey, [1] könnte 2Pkt sein, [2] = 3Pkt, [3] = PF
            if (plainNumbers.length > 1 && !player.twos) player.twos = Number(plainNumbers[1]);
            if (plainNumbers.length > 2 && !player.threes) player.threes = Number(plainNumbers[2]);
            if (plainNumbers.length > 3 && !player.fouls) player.fouls = Number(plainNumbers[3]);
          }
          
          // +/- erkennen (letztes Zeichen, kann negativ sein)
          const plusMinusMatch = ln.match(/([\-+]?\d{1,2})\s*$/);
          if (plusMinusMatch) {
            const val = plusMinusMatch[1];
            if (val.includes('-') || val.includes('+') || (plainNumbers && plainNumbers.length > 4)) {
              player.plus_minus = Number(val);
            }
          }
        }
      }
      
      // Mapping speichern
      setJerseyMapping(tempMapping);
      
      // Alle erkannten Spieler in Array konvertieren
      const rows = Array.from(allPlayerData.values()).map(p => ({
        jersey: p.jersey || "",
        name: p.name || "",
        player_id: p.player_id || "",
        game_id: p.game_id || gameMeta.event_id,
        minutes: p.minutes || 0,
        points: p.points || 0,
        plus_minus: p.plus_minus || 0,
        fouls: p.fouls || 0,
        twos: p.twos || 0,
        threes: p.threes || 0,
        free_throws_made: p.free_throws_made || 0,
        free_throws_att: p.free_throws_att || 0,
        source: "multi-ocr"
      }));
      
      setImageResult(rows);
      setStatus(`✅ ${rows.length} Spieler erkannt aus ${files.length} Screenshot(s). Bitte überprüfen und bei Bedarf korrigieren.`);
      
    } catch (err) {
      setStatus(`❌ Fehler beim Verarbeiten: ${err.message}`);
    }
    
    setProcessingScreenshots(false);
  };

  const updateImageRow = (idx, patch) => { setImageResult(prev => prev.map((r,i)=> i===idx? {...r, ...patch}: r)); };

  // NOTE: ensureEvent is no longer used - event creation/update goes through backend API
  // This function is kept for backwards compatibility but should not be called directly
  const ensureEvent = async () => {
    // If event_id is already set, return it
    if (gameMeta.event_id) return gameMeta.event_id;
    // Otherwise, the backend will create the event during stats import
    return null;
  };

  const saveStats = async (rows) => {
    // SECURITY: Only use backend API - no direct Firestore fallback
    // Backend handles: auth verification, upsert logic, validation
    
    try {
      const user = await import("@/config/firebase").then(m => m.auth.currentUser);
      if (!user) {
        setStatus("❌ Nicht eingeloggt - bitte zuerst einloggen");
        return;
      }
      
      const token = await user.getIdToken();
      
      // Map rows to backend format with jersey_number
      const backendRows = rows.map(r => ({
        name: r.name || 'Player',
        jersey_number: r.jersey || '',
        minutes: typeof r.minutes === 'string' && r.minutes.includes(':') 
          ? r.minutes : String(r.minutes || 0),
        points: Number(r.points || 0),
        fw_made: Number(r.free_throws_made || 0),
        fw_attempts: Number(r.free_throws_att || 0),
        twos: Number(r.twos || 0),
        threes: Number(r.threes || 0),
        pf: Number(r.fouls || 0),
        plus_minus: Number(r.plus_minus || 0),
      }));
      
      // Determine event_id - either existing or new
      let eventId = gameMeta.event_id;
      
      const res = await fetch(`${BACKEND_URL}/api/admin/stats/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: eventId || null,
          event_title: gameMeta.title || (gameMeta.opponent ? `vs ${gameMeta.opponent}` : 'Spiel'),
          opponent: gameMeta.opponent || '',
          start_datetime: gameMeta.start_datetime || '',
          final_home: Number(gameMeta.final_home_score || 0),
          final_away: Number(gameMeta.final_away_score || 0),
          rows: backendRows,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        const created = data.created || 0;
        const updated = data.updated || 0;
        
        // Update event_id if backend created a new event
        if (data.event_id && !eventId) {
          setGameMeta(m => ({ ...m, event_id: data.event_id }));
        }
        
        // Handle validation warnings
        let statusMsg = `✅ Import erfolgreich: ${created} neu, ${updated} aktualisiert`;
        
        if (data.validation_warnings && data.validation_warnings.length > 0) {
          statusMsg += `\n⚠️ Warnungen: ${data.validation_warnings.join('; ')}`;
        }
        
        if (!data.points_validation_passed) {
          statusMsg += `\n⚠️ Punktevalidierung: Spieler-Summe (${data.total_player_points}) ≠ Team-Ergebnis (${data.expected_team_score})`;
        }
        
        // Handle unresolved players
        if (data.unresolved && data.unresolved.length > 0) {
          setUnresolvedPlayers(data.unresolved);
          statusMsg += `\n⚠️ ${data.unresolved.length} Spieler benötigen Zuordnung`;
        }
        
        setStatus(statusMsg);
      } else {
        // Handle auth errors gracefully
        if (res.status === 401) {
          setStatus("❌ Nicht authentifiziert - bitte neu einloggen");
        } else if (res.status === 403) {
          setStatus("❌ Keine Admin-Berechtigung für Stats-Import");
        } else {
          const err = await res.json().catch(() => ({}));
          setStatus(`❌ Import fehlgeschlagen: ${err.detail || res.status}`);
        }
      }
    } catch (err) {
      console.error("Stats import error:", err);
      setStatus(`❌ Import-Fehler: ${err.message}`);
    }
  };

  const saveFinal = async () => {
    // SECURITY: Use backend API to update event score
    try {
      const user = await import("@/config/firebase").then(m => m.auth.currentUser);
      if (!user) {
        setStatus("❌ Nicht eingeloggt - bitte zuerst einloggen");
        return;
      }
      
      if (!gameMeta.event_id) {
        setStatus("❌ Keine Event-ID angegeben - bitte Event-ID eingeben oder Stats mit Metadaten importieren");
        return;
      }
      
      const token = await user.getIdToken();
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${gameMeta.event_id}/score`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          final_home_score: Number(gameMeta.final_home_score || 0),
          final_away_score: Number(gameMeta.final_away_score || 0),
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(`✅ Ergebnis aktualisiert: ${data.final_home_score}:${data.final_away_score}`);
      } else if (res.status === 401) {
        setStatus("❌ Nicht authentifiziert - bitte neu einloggen");
      } else if (res.status === 403) {
        setStatus("❌ Keine Admin-Berechtigung");
      } else if (res.status === 404) {
        setStatus("❌ Event nicht gefunden");
      } else {
        const err = await res.json().catch(() => ({}));
        setStatus(`❌ Fehler: ${err.detail || res.status}`);
      }
    } catch (err) {
      console.error("Save final error:", err);
      setStatus(`❌ Fehler: ${err.message}`);
    }
  };

  const loadFromDBB = async () => {
    if (!dbbUrl) return;
    setLoadingDBB(true); setStatus('Lade DBB-Seite…');
    try {
      const res = await fetch(`${BACKEND_URL}/api/scrape/dbb`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ url: dbbUrl }) });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      const rows = parseFromDBBHtml(data.html||'');
      setImageResult(rows);
      setStatus(`DBB-Link geladen. Erkannte Zeilen: ${rows.length}.`);
    } catch (e) { setStatus('Konnte DBB-Link nicht laden.'); }
    setLoadingDBB(false);
  };

  const rowsCSV = useMemo(()=>{ try { const out = Papa.parse(csvText, { header:true, skipEmptyLines:true }); return out.data || []; } catch { return []; } }, [csvText]);
  const rowsJSON = useMemo(()=>{ try { return JSON.parse(jsonText || "[]"); } catch { return []; } }, [jsonText]);

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-4">Vergangene Spiele & Stats importieren</h2>

      <Card className="bg-[#1a0d11] border-white/10 mb-4">
        <CardContent className="pt-4 space-y-2">
          <h3 className="font-semibold">Spiel-Metadaten (für vergangenes Spiel)</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Input placeholder="Event-ID (leer = neu anlegen)" value={gameMeta.event_id} onChange={e=>setGameMeta(m=>({...m,event_id:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Titel (optional)" value={gameMeta.title} onChange={e=>setGameMeta(m=>({...m,title:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Datum/Zeit (YYYY-MM-DDTHH:mm)" value={gameMeta.start_datetime} onChange={e=>setGameMeta(m=>({...m,start_datetime:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Gegner" value={gameMeta.opponent} onChange={e=>setGameMeta(m=>({...m,opponent:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Liga (optional)" value={gameMeta.league} onChange={e=>setGameMeta(m=>({...m,league:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Ort/Halle" value={gameMeta.location} onChange={e=>setGameMeta(m=>({...m,location:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Endstand Heim" value={gameMeta.final_home_score} onChange={e=>setGameMeta(m=>({...m,final_home_score:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Endstand Auswärts" value={gameMeta.final_away_score} onChange={e=>setGameMeta(m=>({...m,final_away_score:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
            <Input placeholder="Technische Fouls" value={gameMeta.final_technicals} onChange={e=>setGameMeta(m=>({...m,final_technicals:e.target.value}))} className="bg-[#2a1419] border-white/10 text-rose-50" />
          </div>
          <div className="flex gap-2"><Button onClick={saveFinal} className="bg-[#b40023] hover:bg-[#97001d]">Ergebnis speichern (Event anlegen/aktualisieren)</Button></div>
        </CardContent>
      </Card>

      <Card className="bg-[#1a0d11] border-white/10 mb-2">
        <CardContent className="pt-4 space-y-2">
          <h3 className="font-semibold">DBB-Link (Beta)</h3>
          <div className="grid gap-2 md:grid-cols-3">
            <Input placeholder="https://dbb.nbn23.com/..." value={dbbUrl} onChange={e=>setDbbUrl(e.target.value)} className="bg-[#2a1419] border-white/10 text-rose-50 md:col-span-2" />
            <Button onClick={loadFromDBB} disabled={loadingDBB || !dbbUrl} className="bg-white/10 hover:bg-white/20">{loadingDBB? 'Lade…':'Daten laden'}</Button>
          </div>
          <div className="text-xs opacity-70">Hinweis: Beta-Heuristik. Für perfekte Ergebnisse bitte Screenshots hochladen oder CSV/JSON nutzen.</div>
          {status && <div className="text-xs mt-1 text-rose-200/80" data-testid="dbb-status">{status}</div>}
        </CardContent>
      </Card>

      {/* Erkannte Zeilen (DBB oder OCR) */}
      <Card className="bg-[#1a0d11] border-white/10 mb-4">
        <CardContent className="pt-4 space-y-3">
          <h3 className="font-semibold">📸 Screenshot-Import (Intelligent)</h3>
          <div className="text-xs text-rose-200/70 space-y-1">
            <p>✨ <strong>Mehrere Screenshots gleichzeitig hochladen:</strong></p>
            <ul className="list-disc list-inside pl-2 space-y-0.5">
              <li>Screenshot 1: M / Pkt / FW / FW% → erkennt <strong>Namen + Trikotnummern</strong></li>
              <li>Screenshot 2: FW% / 2Pkt / 3Pkt / PF → ordnet automatisch über Trikotnummer zu</li>
              <li>Screenshot 3: 2Pkt / 3Pkt / PF / +/- → ordnet automatisch über Trikotnummer zu</li>
            </ul>
            <p className="mt-2">Das System kombiniert alle Daten intelligent. Bitte danach überprüfen!</p>
          </div>
          
          <div className="flex items-center gap-2">
            <input 
              type="file" 
              accept="image/*" 
              multiple 
              onChange={onMultipleImages} 
              disabled={processingScreenshots}
              className="text-sm"
              data-testid="stats-multi-ocr-upload" 
            />
            {processingScreenshots && <span className="text-xs text-rose-200/70">⏳ Verarbeite...</span>}
          </div>
          
          {jerseyMapping.size > 0 && (
            <div className="text-xs text-green-400/80 p-2 bg-green-950/20 rounded border border-green-500/20">
              ✓ Trikotnummern-Mapping aktiv: {jerseyMapping.size} Spieler erkannt
            </div>
          )}

          {/* Unresolved Players - Need Mapping */}
          {unresolvedPlayers.length > 0 && (
            <div className="p-3 bg-yellow-900/20 rounded border border-yellow-500/30 space-y-2">
              <div className="text-sm font-medium text-yellow-300">
                ⚠️ {unresolvedPlayers.length} Spieler benötigen Zuordnung
              </div>
              <div className="space-y-2">
                {unresolvedPlayers.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-yellow-400">#{p.jersey_number || '??'}</span>
                    <span className="text-white/70">({p.dbb_name})</span>
                    <select
                      value={mappingSelections[p.jersey_number] || ''}
                      onChange={(e) => setMappingSelections(prev => ({
                        ...prev,
                        [p.jersey_number]: e.target.value
                      }))}
                      className="flex-1 p-1 rounded bg-[#2a1419] text-xs"
                    >
                      <option value="">Spieler wählen...</option>
                      {users.map(u => (
                        <option key={u.id} value={u.auth_uid || u.id}>
                          {u.display_name || u.name || u.email}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      onClick={() => {
                        const selectedUid = mappingSelections[p.jersey_number];
                        if (selectedUid) {
                          savePlayerMapping(p.jersey_number, selectedUid, p.dbb_name);
                        }
                      }}
                      disabled={!mappingSelections[p.jersey_number]}
                      className="bg-yellow-600 hover:bg-yellow-700 text-xs h-7"
                    >
                      Speichern
                    </Button>
                  </div>
                ))}
              </div>
              <div className="text-xs text-white/50">
                Nach dem Speichern werden diese Mappings für zukünftige Importe verwendet.
              </div>
            </div>
          )}

          {/* Persisted Mappings Info */}
          {persistedMappings.length > 0 && (
            <div className="text-xs text-blue-400/80 p-2 bg-blue-950/20 rounded border border-blue-500/20">
              📋 {persistedMappings.length} persistierte Mappings geladen (Saison 2026-27)
            </div>
          )}
          
          {imageResult.length>0 && (
            <div className="text-sm text-rose-200/90 font-medium">
              📋 {imageResult.length} Spieler erkannt - Bitte Daten überprüfen:
            </div>
          )}
          {imageResult.length>0 && (
            <div className="overflow-x-auto rounded border border-white/10">
              <table className="w-full text-xs">
                <thead className="bg-[#160b0f] text-rose-200/80">
                  <tr>
                    <th className="p-2 text-left">Nr</th>
                    <th className="p-2 text-left">Name (erkannt)</th>
                    <th className="p-2 text-left">Spieler-Zuordnung</th>
                    <th className="p-2 text-left">Min</th>
                    <th className="p-2 text-left">Pts</th>
                    <th className="p-2 text-left">+/‑</th>
                    <th className="p-2 text-left">PF</th>
                    <th className="p-2 text-left">2P</th>
                    <th className="p-2 text-left">3P</th>
                    <th className="p-2 text-left">FW m/a</th>
                  </tr>
                </thead>
                <tbody>
                  {imageResult.map((r,idx)=> (
                    <tr key={idx} className="border-t border-white/10 hover:bg-white/5">
                      <td className="p-2 font-mono text-rose-300">{r.jersey || '??'}</td>
                      <td className="p-2">{r.name||'Unbekannt'}</td>
                      <td className="p-2">
                        <select value={r.player_id||""} onChange={e=>updateImageRow(idx,{ player_id: e.target.value, game_id: gameMeta.event_id })} className="p-1 rounded bg-[#2a1419] text-xs w-full">
                          <option value="">Spieler wählen…</option>
                          {users.map(u=> (<option key={u.id} value={u.auth_uid||u.id}>{u.display_name||u.name||u.email}</option>))}
                        </select>
                      </td>
                      <td className="p-2"><Input value={r.minutes||0} onChange={e=>updateImageRow(idx,{ minutes: e.target.value })} className="h-7 bg-[#2a1419] w-16" /></td>
                      <td className="p-2"><Input value={r.points||0} onChange={e=>updateImageRow(idx,{ points: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-14" /></td>
                      <td className="p-2"><Input value={r.plus_minus||0} onChange={e=>updateImageRow(idx,{ plus_minus: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-14" /></td>
                      <td className="p-2"><Input value={r.fouls||0} onChange={e=>updateImageRow(idx,{ fouls: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-14" /></td>
                      <td className="p-2"><Input value={r.twos||0} onChange={e=>updateImageRow(idx,{ twos: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-14" /></td>
                      <td className="p-2"><Input value={r.threes||0} onChange={e=>updateImageRow(idx,{ threes: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-14" /></td>
                      <td className="p-2 flex items-center gap-1">
                        <Input value={r.free_throws_made||0} onChange={e=>updateImageRow(idx,{ free_throws_made: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-12" />
                        <span>/</span>
                        <Input value={r.free_throws_att||0} onChange={e=>updateImageRow(idx,{ free_throws_att: Number(e.target.value||0) })} className="h-7 bg-[#2a1419] w-12" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={()=>saveStats(imageResult)} disabled={imageResult.length === 0} className="bg-[#b40023] hover:bg-[#97001d]">
              ✅ Erkannte Stats speichern
            </Button>
            {imageResult.length > 0 && (
              <Button onClick={()=>{setImageResult([]); setJerseyMapping(new Map()); setStatus("");}} variant="outline" className="border-white/20 hover:bg-white/10">
                🔄 Zurücksetzen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1a0d11] border-white/10 mb-4">
        <CardContent className="pt-4 space-y-2">
          <h3 className="font-semibold">CSV / JSON</h3>
          <textarea value={csvText} onChange={e=>setCsvText(e.target.value)} placeholder="CSV-Statistiken hier einfügen (player_id,game_id,minutes,points,plus_minus,fouls,twos,threes,free_throws_made,free_throws_att,source)" className="w-full h-32 p-2 rounded bg-[#2a1419] border-white/10 text-rose-50"></textarea>
          <Button onClick={()=>saveStats(parseCSV())} className="bg-[#b40023] hover:bg-[#97001d]">CSV speichern</Button>
          <textarea value={jsonText} onChange={e=>setJsonText(e.target.value)} placeholder='[ {"player_id": "...", "minutes": 20, ...} ]' className="w-full h-32 p-2 rounded bg-[#2a1419] border-white/10 text-rose-50 mt-3"></textarea>
          <Button onClick={()=>saveStats(parseJSON())} className="bg-[#b40023] hover:bg-[#97001d]">JSON speichern</Button>
        </CardContent>
      </Card>

      {status && <div className="mt-3 text-sm text-rose-200/80">{status}</div>}
    </div>
  );
}
